<template>
  <div v-if="internalPrice" class="row">
    <div class="column price-type">
      <Select v-model="priceType" :options="priceTypes" option-label="name" option-value="value" :disabled="disabled">
        <template #value="slotProps">
          <div v-if="slotProps.value" class="row">
            <VdIcon :name="getIconName(slotProps.value)" store="fas"></VdIcon>
            <div>{{ getSelectedName(slotProps.value) }}</div>
          </div>
          <span v-else>Select pricing type</span>
        </template>

        <template #option="slotProps">
          <div class="row">
            <VdIcon :name="getIconName(slotProps.option.value)" store="fas"></VdIcon>
            <div>{{ slotProps.option.name }}</div>
          </div>
        </template>
      </Select>
    </div>
  </div>
  <div v-if="nonConsumableIds.length > 0" :class="['row mt-1', $style.infoMessage]">
    <VdIcon store="fas" name="circle-info" color="black" />
    <span>A non-consumable is selected, so only IAPs associated with this non-consumable will be available</span>
  </div>
  <div class="row mt-1">
    <div :class="{ column: true, 'full-width': isIAPPricing }">
      <div v-if="isIAPPricing">
        <IAPSelectDropdown
          :model-value="internalPrice.IAP.ios.id"
          :platform="Platform.Ios"
          :game-id="gameId"
          :version-id="versionId"
          :non-consumable-ids="nonConsumableIds"
          @update="IAPPriceUpdate"
        ></IAPSelectDropdown>
      </div>
      <div v-if="isInGameCurrencyPricing">
        <InGameCurrencyDropDown
          :model-value="internalPrice.inGameCurrency.itemId"
          :game-id="gameId"
          :version-id="versionId"
          @update="handleInGameCurrencyUpdate"
        ></InGameCurrencyDropDown>
      </div>
      <div v-if="isRVPricing">
        <InputNumber
          v-model="internalPrice.RV.quantity"
          :min="1"
          :max-fraction-digits="0"
          :min-fraction-digits="0"
          :input-class="$style.inputQuantity"
          @update:model-value="handleRVCurrencyQuantityUpdate"
        ></InputNumber>
      </div>
    </div>
  </div>
  <div v-if="hasTwoColumnsPrice" class="row mt-1">
    <div :class="{ column: true, 'full-width': isIAPPricing }">
      <div v-if="isIAPPricing" style="width: 100%">
        <IAPSelectDropdown
          :model-value="price.IAP.android.id"
          :platform="Platform.Android"
          :game-id="gameId"
          :version-id="versionId"
          :non-consumable-ids="nonConsumableIds"
          @update="IAPPriceUpdate"
        ></IAPSelectDropdown>
      </div>
      <div v-if="isInGameCurrencyPricing">
        <InputNumber
          v-model="internalPrice.inGameCurrency.quantity"
          :min="0"
          :max-fraction-digits="0"
          :min-fraction-digits="0"
          :input-class="$style.inputQuantity"
          @update:model-value="handleInGameCurrencyQuantityUpdate"
        ></InputNumber>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import IAPSelectDropdown from "@/components/IAPSelectDropdown";
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { Platform } from "@/graphql";
import { PriceInput } from "@/modules/Store/shop.types";
import InputNumber from "primevue/inputnumber";
import Select from "primevue/select";
import type { PropType } from "vue";
import { computed, ref, watch } from "vue";
import InGameCurrencyDropDown from "./InGameCurrencyDropDown.vue";

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },
  versionId: {
    type: String,
    required: true,
  },
  price: {
    type: Object as PropType<PriceInput>,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  selectedPriceType: {
    type: String,
    default: null,
  },
  nonConsumableIds: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
});

const emits = defineEmits(["price-type-update", "price-update"]);

const priceType = computed({
  get: () => props.selectedPriceType,
  set: (value) => {
    emits("price-type-update", value);
  },
});

const priceTypes = ref([
  { name: "Payment", value: "IAP" },
  { name: "In game currency", value: "IN_GAME_CURRENCY" },
  { name: "RV", value: "RV" },
  { name: "FREE", value: "FREE" },
]);

const internalPrice = ref<PriceInput>(null);
const getIconName = (name: string) => {
  switch (name) {
    case "IAP":
      return "money-bill";
    case "IN_GAME_CURRENCY":
      return "box";
    case "RV":
      return "clapperboard-play";
    case "FREE":
      return "box";
    default:
      return "";
  }
};

const getSelectedName = (value: string) => {
  const selected = priceTypes.value.find((type) => type.value === value);
  return selected ? selected.name : "";
};

const hasTwoColumnsPrice = computed(() => priceType.value === "IAP" || priceType.value === "IN_GAME_CURRENCY");

const isIAPPricing = computed(() => priceType.value === "IAP");
const isInGameCurrencyPricing = computed(() => priceType.value === "IN_GAME_CURRENCY");
const isRVPricing = computed(() => priceType.value === "RV");

const emitUpdatedPrice = () => {
  emits("price-update", JSON.parse(JSON.stringify(internalPrice.value) as PriceInput));
};

const IAPPriceUpdate = (event: { id: string; platform: Platform }) => {
  if (event.platform === Platform.Ios) {
    internalPrice.value.IAP.ios.id = event.id;
  } else {
    internalPrice.value.IAP.android.id = event.id;
  }

  internalPrice.value.priceType = props.selectedPriceType;

  emitUpdatedPrice();
};

const handleInGameCurrencyUpdate = (event: { id: string }) => {
  internalPrice.value.priceType = props.selectedPriceType;
  internalPrice.value.inGameCurrency.itemId = event.id;
  emitUpdatedPrice();
};

const handleInGameCurrencyQuantityUpdate = (value: number) => {
  internalPrice.value.priceType = props.selectedPriceType;
  internalPrice.value.inGameCurrency.quantity = value;
  emitUpdatedPrice();
};

const handleRVCurrencyQuantityUpdate = (value: number) => {
  internalPrice.value.priceType = props.selectedPriceType;
  internalPrice.value.RV.quantity = value;
  emitUpdatedPrice();
};

watch(
  () => props.price,
  (newPrice) => {
    internalPrice.value = { ...newPrice };
  },
  {
    immediate: true,
  }
);
</script>
<style scoped lang="scss">
.row {
  display: flex;
  gap: 1rem;
  flex-direction: row;
}

.column {
  display: flex;
  gap: 1rem;
  flex-direction: column;
}
.price-type {
  width: 100%;
}
.mt-1 {
  margin-top: 1rem;
}
.full-width {
  width: 100%;
}
</style>
<style module lang="scss">
.inputQuantity {
  width: 127px;
}
.infoMessage {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  margin-top: 0.5rem;
  border-radius: 0.5rem;
  background-color: var(--vd-color-blue-light-9);
  font-size: 12px;

  svg {
    margin-right: 0.75rem;
  }
}
</style>
